import { Helmet } from 'react-helmet-async';

const SITE_NAME = 'Graspd';
const DEFAULT_TITLE = 'Graspd - AI Learning That Adapts to Your Style | Personalized Courses';
const DEFAULT_DESCRIPTION = 'Transform how you learn with Graspd\'s AI-powered adaptive learning platform. Upload PDFs, describe topics, and get personalized courses tailored to your learning style. Master any subject faster.';
const SITE_URL = 'https://graspd.ai';
const DEFAULT_OG_IMAGE = `${SITE_URL}/assets/og-image.png`;
const TWITTER_HANDLE = '@graspd_ai';

/**
 * SEO component for managing page-level meta tags
 *
 * @param {Object} props
 * @param {string} props.title - Page title (will be appended with " | Graspd")
 * @param {string} props.description - Meta description for the page
 * @param {string} props.path - URL path (e.g., "/pricing")
 * @param {string} props.ogImage - Open Graph image URL
 * @param {string} props.ogType - Open Graph type (default: "website")
 * @param {boolean} props.noindex - If true, adds noindex meta tag
 * @param {Object} props.schema - JSON-LD schema object to inject
 * @param {string} props.keywords - Additional keywords for this page
 */
export default function SEO({
  title,
  description = DEFAULT_DESCRIPTION,
  path = '',
  ogImage = DEFAULT_OG_IMAGE,
  ogImageWidth = 1200,
  ogImageHeight = 630,
  ogImageAlt,
  ogType = 'website',
  noindex = false,
  schema,
  keywords,
}) {
  const fullTitle = title ? `${title} | ${SITE_NAME}` : DEFAULT_TITLE;
  const canonicalUrl = `${SITE_URL}${path}`;
  const imageAlt = ogImageAlt || fullTitle;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={canonicalUrl} />

      {/* Robots */}
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content={String(ogImageWidth)} />
      <meta property="og:image:height" content={String(ogImageHeight)} />
      <meta property="og:image:alt" content={imageAlt} />
      <meta property="og:site_name" content={SITE_NAME} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={TWITTER_HANDLE} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      <meta name="twitter:image:alt" content={imageAlt} />

      {/* JSON-LD Schema */}
      {schema && (
        <script type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      )}
    </Helmet>
  );
}

// Pre-defined page configs for consistency
const ENTERPRISE_DESCRIPTION = 'Graspd Enterprise turns your SOPs and work instructions into auditable, traceable internal training, automatically, without your content ever leaving your control. Built for manufacturers proving compliance under IATF 16949, ISO 9001, and OSHA.';
const ENTERPRISE_OG_IMAGE = `${SITE_URL}/assets/enterprise-twitter-card.png`;

// Homepage is the enterprise landing. `/enterprise` 301s here so old share
// links unfurl this same card.
const ENTERPRISE_SEO = {
  title: 'Enterprise',
  description: ENTERPRISE_DESCRIPTION,
  path: '/',
  ogImage: ENTERPRISE_OG_IMAGE,
  ogImageWidth: 1200,
  ogImageHeight: 628,
  ogImageAlt: 'Graspd Engine — personalized training from your documents',
  keywords: 'enterprise compliance training platform, audit-ready training, IATF 16949 training, ISO 9001 compliance records, manufacturing compliance software, traceable training records, internal training automation',
};

export const SEO_CONFIG = {
  home: ENTERPRISE_SEO,
  academy: {
    title: 'Graspd Academy: Mental Fitness in the Age of AI',
    description: 'A gym for your mind. Graspd Academy trains six pillars of thinking in short daily sessions, and tracks your progress with the G-Score, built on established cognitive tests.',
    path: '/academy',
    ogImage: DEFAULT_OG_IMAGE,
    ogImageAlt: 'Graspd — the AI course building engine',
    keywords: 'Graspd Academy, AI academy tutor, AI course builder, AI curriculum builder, self-paced learning, academic transcript app, AI tutor for kids, PDF to course, learning records',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: 'Graspd',
      applicationCategory: 'EducationalApplication',
      operatingSystem: 'iOS, Web',
      description: 'AI-powered course builder for Graspd Academy. Type a topic or upload a PDF to generate lessons, quizzes, and an AI tutor, with a transcript that builds itself by subject and academic year.',
      url: 'https://graspd.ai/academy',
      offers: {
        '@type': 'Offer',
        price: '15.00',
        priceCurrency: 'USD',
      },
    },
  },
  pricing: {
    title: 'Pricing',
    description: 'Choose the perfect Graspd plan for your learning journey. Start free or unlock unlimited AI-powered courses with our Pro plan. Cancel anytime.',
    path: '/pricing',
    keywords: 'Graspd pricing, AI learning subscription, online course pricing, personalized learning cost',
  },
  login: {
    title: 'Sign In',
    description: 'Sign in to your Graspd account to continue your personalized learning journey.',
    path: '/login',
    noindex: true, // Don't index login page
  },
  dashboard: {
    title: 'Dashboard',
    description: 'Your Graspd learning dashboard. Track progress, continue courses, and start new learning journeys.',
    path: '/dashboard',
    noindex: true, // Protected page
  },
  insights: {
    title: 'Learning Insights',
    description: 'View your learning analytics and insights on Graspd.',
    path: '/insights',
    noindex: true, // Protected page
  },
  courseRequest: {
    title: 'Create Course',
    description: 'Create a new personalized course with Graspd AI.',
    path: '/request',
    noindex: true, // Protected page
  },
  privacy: {
    title: 'Privacy Policy',
    description: 'Learn how Graspd collects, uses, and protects your information when you use our AI-powered learning platform.',
    path: '/privacy',
  },
  terms: {
    title: 'Terms of Service',
    description: 'Read the Terms of Service for Graspd, the AI-powered learning platform. Understand your rights and responsibilities when using our service.',
    path: '/terms',
  },
  help: {
    title: 'Help & Support',
    description: 'Need help with Graspd? Contact our support team for assistance with our AI-powered learning platform.',
    path: '/help',
  },
  enterprise: ENTERPRISE_SEO,
};

// Helper to generate course-specific SEO
export function generateCourseSEO(course) {
  if (!course) return {};

  return {
    title: course.title || 'Course',
    description: course.description || `Learn ${course.title} with Graspd's AI-powered adaptive learning.`,
    path: `/courses/${course.id}`,
    noindex: true, // User-specific content
    schema: {
      '@context': 'https://schema.org',
      '@type': 'Course',
      name: course.title,
      description: course.description,
      url: `https://graspd.ai/courses/${course.id}`,
      provider: {
        '@type': 'Organization',
        name: 'Graspd',
        url: 'https://graspd.ai',
      },
      ...(course.sections && {
        hasCourseInstance: {
          '@type': 'CourseInstance',
          courseMode: 'online',
          courseWorkload: `${course.sections.length} sections`,
        },
      }),
    },
  };
}
